<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    :style="{ width: '28rem' }"
  >
    <template #header>
      <div>
        <div class="flex items-center gap-2.5">
          <h2 class="text-navy-800 text-base font-semibold">
            {{ registration?.name || "Unnamed Agent" }}
          </h2>
          <StatusBadge v-if="registration" :variant="statusBadgeVariant" dot>
            {{ statusLabel }}
          </StatusBadge>
        </div>
        <p class="text-[13px] text-neutral-500">
          Agent management and configuration
        </p>
      </div>
    </template>

    <template v-if="registration">
      <!-- Agent details grid -->
      <div class="grid grid-cols-2 gap-x-5 gap-y-3">
        <div>
          <div class="mb-0.5 text-[11px] text-neutral-400">Hostname</div>
          <div class="text-navy-800 text-[13px]">
            {{ registration.hostname || "---" }}
          </div>
        </div>
        <div>
          <div class="mb-0.5 text-[11px] text-neutral-400">IP Address</div>
          <div class="text-navy-800 text-[13px]">
            {{ registration.ipAddress || "---" }}
          </div>
        </div>
        <div>
          <div class="mb-0.5 text-[11px] text-neutral-400">Version</div>
          <div class="text-navy-800 text-[13px]">
            {{ registration.agentVersion || "---" }}
          </div>
        </div>
        <div>
          <div class="mb-0.5 text-[11px] text-neutral-400">Last Heartbeat</div>
          <div class="text-navy-800 text-[13px]">
            {{ formatHeartbeat(registration.lastHeartbeatAt) }}
          </div>
        </div>
      </div>

      <!-- SoftPro Configuration section -->
      <div class="-mx-6 mt-5 border-t border-neutral-200 px-6 pt-4">
        <h3
          class="mb-3 text-[11px] font-bold tracking-[0.06em] text-neutral-400 uppercase"
        >
          SoftPro Configuration
        </h3>
        <div class="mb-2 grid grid-cols-2 gap-x-5 gap-y-3">
          <div>
            <div class="mb-0.5 text-[11px] text-neutral-400">Server URL</div>
            <div class="text-navy-800 font-mono text-[13px]">
              {{ configServerUrl || "---" }}
            </div>
          </div>
          <div>
            <div class="mb-0.5 text-[11px] text-neutral-400">Profile</div>
            <div class="text-navy-800 text-[13px]">
              {{ configProfile || "---" }}
            </div>
          </div>
        </div>
        <button
          v-if="isAdmin"
          class="text-xs font-medium text-green-600 hover:text-green-700"
          @click="handleReconfigure"
        >
          Change Configuration
        </button>
      </div>

      <!-- Send Back Settings section -->
      <template v-if="isAdmin && registration.status === 'connected'">
        <div class="-mx-6 mt-5 border-t border-neutral-200 px-6 pt-4">
          <h3
            class="mb-3 text-[11px] font-bold tracking-[0.06em] text-neutral-400 uppercase"
          >
            Send Back Settings
          </h3>
          <div class="space-y-4">
            <div>
              <label class="text-navy-800 mb-1 block text-[13px] font-medium">
                Push Mode
              </label>
              <AppSelect
                v-model="pushModeValue"
                :options="pushModeOptions"
                placeholder="Select push mode"
              />
            </div>
            <div v-if="pushModeValue">
              <label class="text-navy-800 mb-1 block text-[13px] font-medium">
                Folder Name
              </label>
              <input
                v-model="pushFolderNameValue"
                type="text"
                placeholder="Default folder"
                class="text-navy-800 placeholder-navy-300 block w-full rounded-md border border-neutral-300 px-3 py-[9px] text-[13px] focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
              />
              <p class="mt-1 text-xs text-neutral-400">
                Documents will be placed in this folder in the source system.
                Created automatically if it doesn't exist.
              </p>
            </div>
          </div>
        </div>
      </template>

      <!-- Settings section -->
      <div class="-mx-6 mt-5 border-t border-neutral-200 px-6 pt-4">
        <h3
          class="mb-3 text-[11px] font-bold tracking-[0.06em] text-neutral-400 uppercase"
        >
          Settings
        </h3>
        <div>
          <label class="text-navy-800 mb-1 block text-[13px] font-medium">
            Agent Name
          </label>
          <input
            v-model="agentNameValue"
            type="text"
            placeholder="e.g. PCT Main Office"
            class="text-navy-800 placeholder-navy-300 w-full rounded-md border border-neutral-300 px-3 py-[9px] text-[13px] focus:border-green-500 focus:ring-2 focus:ring-green-500/20 focus:outline-none"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div v-if="isAdmin" class="flex justify-between">
        <AppButton variant="danger" size="sm" @click="confirmRevoke">
          Revoke Agent
        </AppButton>
        <AppButton :loading="saving" @click="saveChanges">
          Save Changes
        </AppButton>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import Drawer from "primevue/drawer"
import { useConfirm } from "primevue/useconfirm"
import { useToast } from "primevue/usetoast"
import { computed, ref, watch } from "vue"

import type { AgentRegistrationType } from "../../pages/Company/types"
import REVOKE_AGENT from "../../queries/agent_registration/revokeAgent.gql"
import UPDATE_AGENT_NAME from "../../queries/agent_registration/updateName.gql"
import UPDATE_PUSH_CONFIG from "../../queries/agent_registration/updatePushConfig.gql"
import { apolloClient } from "../../queries/client"
import AppButton from "../ui/AppButton.vue"
import AppSelect from "../ui/AppSelect.vue"
import StatusBadge from "../ui/StatusBadge.vue"

interface Props {
  registration: AgentRegistrationType | null
  isAdmin: boolean
}

const props = defineProps<Props>()

const visible = defineModel<boolean>("visible", { default: false })

const emit = defineEmits<{
  reconfigure: [registration: AgentRegistrationType]
  revoked: []
  updated: [registration: AgentRegistrationType]
}>()

const toast = useToast()
const confirm = useConfirm()

const statusBadgeVariant = computed(() => {
  if (props.registration?.status === "connected") return "success"
  if (props.registration?.status === "configuring") return "info"
  return "warning"
})

const statusLabel = computed(() => {
  const labels: Record<string, string> = {
    connected: "Connected",
    disconnected: "Disconnected",
    configuring: "Configuring",
    pairing: "Pairing",
  }
  return labels[props.registration?.status ?? ""] || props.registration?.status
})

// ─── Form State ─────────────────────────────────────────────────

const agentNameValue = ref("")
const pushModeValue = ref("")
const pushFolderNameValue = ref("")
const configServerUrl = ref("")
const configProfile = ref("")
const saving = ref(false)

const pushModeOptions = [
  { label: "Disabled", value: "" },
  { label: "Automatic", value: "auto" },
  { label: "Manual", value: "manual" },
]

watch(
  () => props.registration,
  (reg) => {
    if (reg) {
      agentNameValue.value = reg.name || ""
      pushModeValue.value = reg.pushMode || ""
      pushFolderNameValue.value = reg.pushFolderName || ""
    }
  },
  { immediate: true },
)

// ─── Save All Changes ──────────────────────────────────────────

const saveChanges = async () => {
  if (!props.registration) return
  saving.value = true

  try {
    // Save name
    const nameResult = await apolloClient.mutate({
      mutation: UPDATE_AGENT_NAME,
      variables: {
        agentRegistrationId: props.registration.id,
        name: agentNameValue.value.trim(),
      },
    })
    const nameData = nameResult.data?.updateAgentName
    if (nameData?.errors?.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: nameData.errors.join(", "),
        life: 4000,
      })
      return
    }

    // Save push config
    const mode = pushModeValue.value || null
    const folderName = pushModeValue.value
      ? pushFolderNameValue.value.trim() || null
      : null

    const pushResult = await apolloClient.mutate({
      mutation: UPDATE_PUSH_CONFIG,
      variables: {
        agentRegistrationId: props.registration.id,
        pushMode: mode,
        pushFolderName: folderName,
      },
    })
    const pushData = pushResult.data?.updateAgentPushConfig
    if (pushData?.errors?.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: pushData.errors.join(", "),
        life: 4000,
      })
      return
    }

    toast.add({
      severity: "success",
      summary: "Saved",
      detail: "Agent settings updated",
      life: 3000,
    })

    if (props.registration) {
      emit("updated", {
        ...props.registration,
        name: agentNameValue.value.trim(),
        pushMode: mode,
        pushFolderName: folderName,
      })
    }
  } catch {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to save changes",
      life: 4000,
    })
  } finally {
    saving.value = false
  }
}

// ─── Actions ────────────────────────────────────────────────────

const handleReconfigure = () => {
  if (props.registration) {
    emit("reconfigure", props.registration)
  }
}

const confirmRevoke = () => {
  confirm.require({
    message:
      "This will disconnect the agent and revoke its credentials. Are you sure?",
    header: "Revoke Agent",
    icon: "pi pi-exclamation-triangle",
    acceptClass: "p-button-danger",
    accept: () => {
      void revokeAgent()
    },
  })
}

const revokeAgent = async () => {
  if (!props.registration) return

  try {
    const result = await apolloClient.mutate({
      mutation: REVOKE_AGENT,
      variables: { agentRegistrationId: props.registration.id },
    })
    const data = result.data?.revokeAgent
    if (data?.errors?.length) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: data.errors.join(", "),
        life: 4000,
      })
      return
    }
    toast.add({
      severity: "success",
      summary: "Success",
      detail: "Agent revoked",
      life: 4000,
    })
    emit("revoked")
  } catch {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to revoke agent",
      life: 4000,
    })
  }
}

// ─── Helpers ────────────────────────────────────────────────────

const formatHeartbeat = (isoString: string | null | undefined) => {
  if (!isoString) return "---"
  const date = new Date(isoString)
  const diffMs = Date.now() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  if (diffSecs < 60) return `${diffSecs}s ago`
  const diffMins = Math.floor(diffSecs / 60)
  if (diffMins < 60) return `${diffMins}m ago`
  return date.toLocaleString()
}
</script>
